import { useSyncExternalStore } from "react";

/** The dashboard's languages: Dutch first, the default for everyone. */
export const LANGUAGES = ["nl", "en", "fr"] as const;
export type Language = (typeof LANGUAGES)[number];
export const DEFAULT_LANGUAGE: Language = "nl";

/** Each language's own name, as the switcher shows it. */
export const LANGUAGE_NAMES: Record<Language, string> = {
  nl: "Nederlands",
  en: "English",
  fr: "Français",
};

const STORAGE_KEY = "dc-language";

export function isLanguage(value: unknown): value is Language {
  return typeof value === "string" && (LANGUAGES as readonly string[]).includes(value);
}

/**
 * The language remembered in this browser, else Dutch. The browser's own
 * language is ignored on purpose: a first visit is always Dutch.
 */
function readStoredLanguage(): Language {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    return isLanguage(stored) ? stored : DEFAULT_LANGUAGE;
  } catch {
    // Storage can be blocked (private mode, site data off): fall back to Dutch.
    return DEFAULT_LANGUAGE;
  }
}

let current: Language = readStoredLanguage();
const listeners = new Set<() => void>();

function applyToDocument(language: Language) {
  if (typeof document !== "undefined") {
    document.documentElement.lang = language;
  }
}

applyToDocument(current);

export function getLanguage(): Language {
  return current;
}

/** Switches the language, remembers it in this browser and sets `<html lang>`. */
export function setLanguage(language: Language) {
  if (language === current) {
    return;
  }
  current = language;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, language);
  } catch {
    // Not remembered, but the switch still holds for this visit.
  }
  applyToDocument(language);
  for (const listener of listeners) {
    listener();
  }
}

/** Calls `listener` after every switch; returns the unsubscribe. */
export function subscribeLanguage(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The current language; re-renders the component when it switches. */
export function useLanguage(): Language {
  return useSyncExternalStore(subscribeLanguage, getLanguage, () => DEFAULT_LANGUAGE);
}
